// Fallback shown in admin-only UtilityProfit views when a partner record has no
// canonical backend ID (should never happen for a persisted partner, but we render
// a clear message rather than a broken/blank value if the field is missing/null).
export const UTILITY_PARTNER_ID_UNAVAILABLE_LABEL = 'ID unavailable';

// Prefix used when the ID is shown inline (e.g. next to a partner name/slug) where
// there is no column header to give it context. Omitted for table cells that sit
// under an explicit "ID" column header.
export const UTILITY_PARTNER_ID_LABEL_PREFIX = 'ID: ';

/**
 * Format a UtilityProfit partner's canonical backend ID (`utilityPartners.id`) for
 * display in the admin partner search and edit views (FEAT-E72).
 *
 * We intentionally use the canonical data-model identifier here — not a derived or
 * display-only value like the slug — so support/engineering can cross-reference the
 * exact partner record. This is admin-only; do not surface it in customer-facing UI.
 *
 * @param {number|string|null|undefined} id - The partner's canonical `id` field.
 * @param {object} [options]
 * @param {boolean} [options.withLabel=false] - Prefix the value with "ID: " for
 *   inline contexts that lack an "ID" column header. The missing/null fallback is
 *   returned as-is (never prefixed) so it never reads as "ID: ID unavailable".
 * @returns {string} The display string, or the fallback when the ID is missing.
 */
export default function formatUtilityPartnerId(id, { withLabel = false } = {}) {
  if (id === null || id === undefined || id === '') {
    return UTILITY_PARTNER_ID_UNAVAILABLE_LABEL;
  }

  // String() keeps this robust for long or non-numeric IDs even though the current
  // schema uses an auto-increment integer; the UI wraps rather than truncates.
  const value = String(id);

  return withLabel ? `${UTILITY_PARTNER_ID_LABEL_PREFIX}${value}` : value;
}
